import { useEffect, useState } from 'react';
import { getAccessToken } from '../services/auth';

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) || '/api';

/**
 * Module-scope cache keyed by `${domain}:${size}` → blob URL. Domain favicons
 * are cross-tenant (shared cache), so the blob is reused across brands + tenants
 * in the same browser session. Mirrors useBrandFavicon but keyed by domain.
 */
const cache = new Map<string, string>();
const inflight = new Map<string, Promise<string | null>>();
// Domains that returned a definitive 404 (no favicon exists) — don't refetch
// these. A NULL from a transient/slow first call is NOT recorded here, so it
// retries on the next mount (the endpoint lazily backfills S3 on first miss,
// which can take a few seconds — we must not give up permanently on that).
const noFavicon = new Set<string>();

async function fetchBlob(domain: string, size: 64 | 128): Promise<string | null | '404'> {
  const token = await getAccessToken();
  const res = await fetch(
    `${API_BASE}/domain-favicon?domain=${encodeURIComponent(domain)}&size=${size}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (res.status === 404) return '404'; // definitively no favicon — stop retrying
  if (!res.ok) return null; // transient (5xx/timeout) — allow retry later
  const ct = res.headers.get('Content-Type') || '';
  if (!ct.startsWith('image/')) return null;
  return URL.createObjectURL(await res.blob());
}

/** `<img src>` for a domain's favicon, or null while loading / unavailable. */
export function useDomainFavicon(
  domain: string | undefined,
  size: 64 | 128 = 64,
): string | null {
  const key = domain ? `${domain}:${size}` : '';
  const [src, setSrc] = useState<string | null>(domain ? cache.get(key) || null : null);

  useEffect(() => {
    if (!domain) return;
    const cached = cache.get(key);
    if (cached) {
      setSrc(cached);
      return;
    }
    if (noFavicon.has(key)) return; // known-absent — keep the letter fallback
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const attempt = (retriesLeft: number) => {
      const load = inflight.get(key) || fetchBlob(domain!, size);
      inflight.set(key, load);
      load
        .then((url) => {
          if (cancelled) return;
          if (url === '404') {
            noFavicon.add(key);
            return;
          }
          if (url) {
            cache.set(key, url);
            setSrc(url);
            return;
          }
          // null = transient / endpoint still backfilling S3 on first miss.
          // Retry a couple times with backoff before falling back to the letter.
          if (retriesLeft > 0) {
            retryTimer = setTimeout(() => attempt(retriesLeft - 1), 2500);
          }
        })
        .finally(() => inflight.delete(key));
    };
    attempt(2);

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [domain, size, key]);

  return src;
}
